import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { generateId, ensureUniqueSlug } from "@/lib/dashboardStore";
import { useDashboardsQuery } from "@/lib/queries";
import { useDataService } from "@/lib/dataService";
import type { Dashboard } from "@/types/dashboard";

/* Manage dashboard resource with React Query */
export function useDashboardStore() {
  const ds = useDataService();
  const queryClient = useQueryClient();
  const { data: apiDashboards = [] } = useDashboardsQuery();

  const createDashboard = useCallback(
    async (name: string, description?: string): Promise<Dashboard> => {
      const now = new Date().toISOString();
      const localConfig: Dashboard = {
        id: "",
        name: name.trim() || "New Dashboard",
        description: description?.trim() || undefined,
        slug: ensureUniqueSlug(
          name.trim() || "new-dashboard",
          apiDashboards.map((d) => d.slug),
        ),
        sources: [],
        status: "draft",
        created_at: now,
        updated_at: now,
        sections: [
          {
            id: generateId("sec"),
            name: "Section 1",
            order: 0,
            collapsed: false,
            placements: [],
          },
        ],
      };

      const config = await ds.createDashboard(localConfig);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      return config;
    },
    [ds, queryClient, apiDashboards],
  );

  const deleteDashboard = useCallback(
    async (id: string) => {
      queryClient.setQueryData<Dashboard[]>(["dashboards"], (prev) =>
        prev ? prev.filter((d) => d.id !== id) : prev,
      );
      try {
        await ds.deleteDashboard(id);
      } catch (err) {
        void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
        throw err;
      }
    },
    [ds, queryClient],
  );

  return { dashboards: apiDashboards, createDashboard, deleteDashboard };
}
